import type { MultiLegDraft } from "./multiLegPosition";

export type PositionExitDteOperator = "lte" | "eq";
export type PositionExitShortStrikeSide = "PUT" | "CALL" | "ANY";
export type PositionExitLogic = "AND" | "OR";

/** Server-derived payoff facts. The browser must never infer these from legs or basis. */
export interface CanonicalPositionPayoffAvailability {
  source: "SERVER_PAYOFF_PREVIEW";
  maxProfitPercent: boolean;
  maxLossPercent: boolean;
  /** Server-computed whole-position denominator, including quantity. */
  maxProfitUsd?: string | null;
  /** Server-computed whole-position denominator, including quantity. */
  maxLossUsd?: string | null;
  maxProfitUnavailableReason?: PositionPayoffUnavailableReason | null;
  maxLossUnavailableReason?: PositionPayoffUnavailableReason | null;
}

export type PositionPayoffUnavailableReason =
  | "SINGLE_OPTION_UNSUPPORTED"
  | "FINITE_PAYOFF_UNAVAILABLE"
  | "ZERO_MAX_PROFIT"
  | "ZERO_MAX_LOSS";

export interface WatchPositionExitDraft {
  logic: PositionExitLogic;
  profitTarget: string;
  lossLimit: string;
  maxProfitPercent: string;
  maxLossPercent: string;
  dteThreshold: string;
  dteOperator: PositionExitDteOperator | null;
  shortStrikeBreach: boolean;
  shortStrikeSide: PositionExitShortStrikeSide | null;
}

export interface PositionExitTriggerAvailability {
  maxProfitPercent: boolean;
  maxLossPercent: boolean;
  shortStrikeSides: readonly Exclude<PositionExitShortStrikeSide, "ANY">[];
}

export interface PositionExitEvaluationSpecV1 {
  schema_version: 1;
  subject: "POSITION_EXIT";
  logic: PositionExitLogic;
  triggers: Array<
    | { id: string; type: "pnl_usd"; operator: "gte" | "lte"; threshold: string }
    | { id: string; type: "max_profit_pct" | "max_loss_pct"; operator: "gte"; threshold: string }
    | { id: string; type: "dte"; operator: PositionExitDteOperator; threshold: number }
    | { id: string; type: "short_strike_breach"; side: PositionExitShortStrikeSide }
  >;
}

const EXACT_NON_NEGATIVE_DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d{1,4})?$/;
const EXACT_PERCENT = /^(?:0|[1-9]\d*)(?:\.\d{1,4})?$/;

/** Return only triggers whose payoff denominator or short leg is defined. */
export function getPositionExitTriggerAvailability(
  draft: MultiLegDraft,
  payoffAvailability?: CanonicalPositionPayoffAvailability,
): PositionExitTriggerAvailability {
  const shortStrikeSides = Array.from(new Set(
    draft.legs
      .filter((leg) => leg.side === "SHORT")
      .map((leg) => leg.optionType === "put" ? "PUT" as const : "CALL" as const),
  ));
  const hasCanonicalMultiLegPayoff = draft.structure !== "single"
    && payoffAvailability?.source === "SERVER_PAYOFF_PREVIEW";
  return {
    maxProfitPercent: hasCanonicalMultiLegPayoff && payoffAvailability?.maxProfitPercent === true,
    maxLossPercent: hasCanonicalMultiLegPayoff && payoffAvailability?.maxLossPercent === true,
    shortStrikeSides,
  };
}

function validateNonNegativeDecimal(value: string, label: string) {
  const normalized = value.trim();
  if (!EXACT_NON_NEGATIVE_DECIMAL.test(normalized)) {
    throw new Error(`${label} must be zero or greater, using up to four decimals.`);
  }
  return normalized;
}

function validatePercent(value: string, label: string) {
  const normalized = value.trim();
  if (!EXACT_PERCENT.test(normalized) || Number(normalized) > 100) {
    throw new Error(`${label} must be between 0 and 100%.`);
  }
  return normalized;
}

/** Build the exact server-owned POSITION_EXIT/v1 authoring contract. */
export function buildPositionExitEvaluationSpec(
  draft: MultiLegDraft,
  values: WatchPositionExitDraft,
  payoffAvailability?: CanonicalPositionPayoffAvailability,
): PositionExitEvaluationSpecV1 {
  const availability = getPositionExitTriggerAvailability(draft, payoffAvailability);
  const triggers: PositionExitEvaluationSpecV1["triggers"] = [];

  if (values.profitTarget.trim()) {
    triggers.push({
      id: "profit-usd",
      type: "pnl_usd",
      operator: "gte",
      threshold: validateNonNegativeDecimal(values.profitTarget, "Profit target"),
    });
  }
  if (values.lossLimit.trim()) {
    triggers.push({
      id: "loss-usd",
      type: "pnl_usd",
      operator: "lte",
      threshold: (() => {
        const threshold = validateNonNegativeDecimal(values.lossLimit, "Loss limit");
        return Number(threshold) === 0 ? threshold : `-${threshold}`;
      })(),
    });
  }
  if (values.maxProfitPercent.trim()) {
    if (!availability.maxProfitPercent) {
      throw new Error("Maximum-profit percentage cannot be used until a server payoff preview confirms a positive finite maximum profit.");
    }
    triggers.push({
      id: "max-profit-percent",
      type: "max_profit_pct",
      operator: "gte",
      threshold: validatePercent(values.maxProfitPercent, "Maximum-profit percentage"),
    });
  }
  if (values.maxLossPercent.trim()) {
    if (!availability.maxLossPercent) {
      throw new Error("Maximum-loss percentage cannot be used until a server payoff preview confirms a positive finite maximum loss.");
    }
    triggers.push({
      id: "max-loss-percent",
      type: "max_loss_pct",
      operator: "gte",
      threshold: validatePercent(values.maxLossPercent, "Maximum-loss percentage"),
    });
  }
  if (values.dteThreshold.trim()) {
    if (!values.dteOperator) throw new Error("Choose how the days-to-expiration alert should match.");
    if (!/^\d{1,4}$/.test(values.dteThreshold.trim())) {
      throw new Error("Days to expiration must be a whole number from 0 to 3650.");
    }
    const threshold = Number(values.dteThreshold.trim());
    if (threshold > 3650) throw new Error("Days to expiration must be a whole number from 0 to 3650.");
    triggers.push({
      id: "days-to-expiry",
      type: "dte",
      operator: values.dteOperator,
      threshold,
    });
  } else if (values.dteOperator) {
    throw new Error("Enter the number of days to expiration.");
  }
  if (values.shortStrikeBreach) {
    if (availability.shortStrikeSides.length === 0) {
      throw new Error("This position has no short strike to monitor.");
    }
    const side = availability.shortStrikeSides.length === 1
      ? availability.shortStrikeSides[0]
      : values.shortStrikeSide;
    if (!side || (side !== "ANY" && !availability.shortStrikeSides.includes(side))) {
      throw new Error("Choose which short strike should trigger the alert.");
    }
    triggers.push({ id: "short-strike", type: "short_strike_breach", side });
  }

  if (triggers.length === 0) throw new Error("Choose at least one position alert.");
  if (triggers.length > 5) throw new Error("Choose up to five position alerts.");
  return { schema_version: 1, subject: "POSITION_EXIT", logic: values.logic, triggers };
}

function shortStrikeForSide(draft: MultiLegDraft, side: "PUT" | "CALL") {
  const optionType = side === "PUT" ? "put" : "call";
  return draft.legs.find((leg) => leg.side === "SHORT" && leg.optionType === optionType)?.strike.trim() || null;
}

export function describeShortStrikeTrigger(
  draft: MultiLegDraft,
  side: PositionExitShortStrikeSide,
) {
  const putStrike = shortStrikeForSide(draft, "PUT");
  const callStrike = shortStrikeForSide(draft, "CALL");
  if (side === "PUT" && putStrike) {
    return `Underlying is at or below the short put strike ($${putStrike})`;
  }
  if (side === "CALL" && callStrike) {
    return `Underlying is at or above the short call strike ($${callStrike})`;
  }
  if (side === "ANY" && putStrike && callStrike) {
    return `Underlying is at or below the short put strike ($${putStrike}) or at or above the short call strike ($${callStrike})`;
  }
  const fallback = side === "ANY" ? "either short strike" : `the short ${side.toLowerCase()} strike`;
  return `Underlying is at or beyond ${fallback}`;
}

export function sharedShortStrikeAnyWarning(
  draft: MultiLegDraft,
  values: Pick<WatchPositionExitDraft, "logic" | "shortStrikeBreach" | "shortStrikeSide">,
) {
  if (draft.structure !== "iron_butterfly"
    || !values.shortStrikeBreach
    || values.shortStrikeSide !== "ANY") return null;
  const putStrike = shortStrikeForSide(draft, "PUT");
  const callStrike = shortStrikeForSide(draft, "CALL");
  if (!putStrike || !callStrike || !Number.isFinite(Number(putStrike))
    || Number(putStrike) !== Number(callStrike)) return null;
  const logicGuidance = values.logic === "AND"
    ? "Any other AND conditions must still be met."
    : "With OR, this condition can make the Watch fire on its first complete, current price check.";
  return `Because both short legs share the $${putStrike} strike, “Either” is true at every sampled underlying price. Choose Put or Call for directional monitoring. ${logicGuidance}`;
}

export function describePositionExitTriggers(
  spec: PositionExitEvaluationSpecV1,
  draft?: MultiLegDraft,
) {
  return spec.triggers.map((trigger) => {
    if (trigger.type === "pnl_usd") {
      return trigger.operator === "gte"
        ? `Estimated profit reaches $${trigger.threshold}`
        : `Estimated loss reaches $${trigger.threshold.startsWith("-") ? trigger.threshold.slice(1) : trigger.threshold}`;
    }
    if (trigger.type === "max_profit_pct") return `Profit reaches ${trigger.threshold}% of maximum profit`;
    if (trigger.type === "max_loss_pct") return `Loss reaches ${trigger.threshold}% of maximum loss`;
    if (trigger.type === "dte") {
      return trigger.operator === "eq"
        ? `Exactly ${trigger.threshold} days remain`
        : `${trigger.threshold} days or fewer remain`;
    }
    if (trigger.type === "short_strike_breach") {
      if (draft) return describeShortStrikeTrigger(draft, trigger.side);
      const side = trigger.side === "ANY" ? "either short strike" : `the short ${trigger.side.toLowerCase()} strike`;
      return `Underlying is at or beyond ${side}`;
    }
    return "Position alert";
  });
}

export function describePositionExitDraft(
  draft: MultiLegDraft,
  values: WatchPositionExitDraft,
  payoffAvailability?: CanonicalPositionPayoffAvailability,
) {
  const emptyValues: WatchPositionExitDraft = {
    logic: values.logic,
    profitTarget: "",
    lossLimit: "",
    maxProfitPercent: "",
    maxLossPercent: "",
    dteThreshold: "",
    dteOperator: null,
    shortStrikeBreach: false,
    shortStrikeSide: null,
  };
  const selections: Array<{ selected: boolean; patch: Partial<WatchPositionExitDraft> }> = [
    { selected: Boolean(values.profitTarget.trim()), patch: { profitTarget: values.profitTarget } },
    { selected: Boolean(values.lossLimit.trim()), patch: { lossLimit: values.lossLimit } },
    { selected: Boolean(values.maxProfitPercent.trim()), patch: { maxProfitPercent: values.maxProfitPercent } },
    { selected: Boolean(values.maxLossPercent.trim()), patch: { maxLossPercent: values.maxLossPercent } },
    {
      selected: Boolean(values.dteThreshold.trim() || values.dteOperator),
      patch: { dteThreshold: values.dteThreshold, dteOperator: values.dteOperator },
    },
    {
      selected: values.shortStrikeBreach,
      patch: { shortStrikeBreach: values.shortStrikeBreach, shortStrikeSide: values.shortStrikeSide },
    },
  ];
  const items: string[] = [];
  const issues: string[] = [];
  for (const selection of selections) {
    if (!selection.selected) continue;
    try {
      const spec = buildPositionExitEvaluationSpec(
        draft,
        { ...emptyValues, ...selection.patch },
        payoffAvailability,
      );
      items.push(...describePositionExitTriggers(spec, draft));
    } catch (reason) {
      issues.push(reason instanceof Error ? reason.message : "Complete this position alert.");
    }
  }
  return { items, issues };
}

/** Count authored trigger groups before contract validation so the UI can enforce the five-trigger cap. */
export function countPositionExitTriggerSelections(values: WatchPositionExitDraft) {
  return [
    values.profitTarget.trim(),
    values.lossLimit.trim(),
    values.maxProfitPercent.trim(),
    values.maxLossPercent.trim(),
    values.dteThreshold.trim() || values.dteOperator,
    values.shortStrikeBreach,
  ].filter(Boolean).length;
}
